import React, { useState, useEffect } from 'react';
import {
  Plus, Mail, ToggleLeft, ToggleRight, Edit2, Trash2,
  Activity, CheckCircle, XCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import autoEmailResponseAPI from '../api/autoEmailResponse';
import templatesAPI from '../api/templates';

const AutoEmailResponses = () => {
  const [autoResponses, setAutoResponses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    trigger_type: 'lead_created',
    email_template: '',
    is_active: true,
    send_to_lead: true,
    send_to_agent: false,
    delay_minutes: 0,
    lead_source_filter: [],
    priority_filter: []
  });
  const [error, setError] = useState('');

  const triggerOptions = [
    { value: 'lead_created', label: 'New Lead Created' },
    { value: 'property_inquiry', label: 'Property Enquiry Received' },
    { value: 'rea_enquiry', label: 'REA Enquiry Received' },
    { value: 'contact_form', label: 'Contact Form Submission' },
    { value: 'callback_request', label: 'Callback Request' },
    { value: 'appraisal_request', label: 'Appraisal Request' }
  ];

  const sourceOptions = ['website', 'referral', 'ofi', 'social_media', 'cold_call', 'chatbot', 'rea', 'domain', 'buyer_portal', 'form', 'other'];
  const priorityOptions = ['low', 'medium', 'high', 'urgent'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [responsesData, templatesData, statsData] = await Promise.all([
        autoEmailResponseAPI.getAll(),
        templatesAPI.getAll(),
        autoEmailResponseAPI.getStats()
      ]);

      setAutoResponses(responsesData.results || []);
      setTemplates(templatesData.user_templates || []);
      setStats(statsData.stats || null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load auto-email responses');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    autoEmailResponseAPI.clearCache();
    templatesAPI.clearCache();
    fetchData();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingRule) {
        await autoEmailResponseAPI.update(editingRule.id, formData);
      } else {
        await autoEmailResponseAPI.create(formData);
      }

      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      console.error('Error saving auto-response:', err);
      setError(err.response?.data?.detail || 'Failed to save auto-response rule');
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      trigger_type: rule.trigger_type,
      email_template: rule.email_template,
      is_active: rule.is_active,
      send_to_lead: rule.send_to_lead,
      send_to_agent: rule.send_to_agent,
      delay_minutes: rule.delay_minutes,
      lead_source_filter: rule.lead_source_filter || [],
      priority_filter: rule.priority_filter || []
    });
    setShowModal(true);
  };

  const handleDelete = (rule) => {
    setRuleToDelete(rule);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!ruleToDelete) return;

    try {
      await autoEmailResponseAPI.delete(ruleToDelete.id);
      setShowDeleteModal(false);
      setRuleToDelete(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting auto-response:', err);
      setError('Failed to delete auto-response rule');
      setShowDeleteModal(false);
      setRuleToDelete(null);
    }
  };

  const handleToggle = async (id) => {
    if (togglingId) return; // Prevent multiple simultaneous toggles

    setTogglingId(id);
    setError(''); // Clear any previous errors

    // Optimistic update - update UI immediately
    setAutoResponses(prevResponses =>
      prevResponses.map(rule =>
        rule.id === id ? { ...rule, is_active: !rule.is_active } : rule
      )
    );

    try {
      const response = await autoEmailResponseAPI.toggle(id);

      // Update with actual server response
      if (response.result) {
        setAutoResponses(prevResponses =>
          prevResponses.map(rule =>
            rule.id === id ? response.result : rule
          )
        );
      }

      // Update stats
      if (stats) {
        setStats({
          ...stats,
          active_rules: response.result.is_active ? stats.active_rules + 1 : stats.active_rules - 1,
          inactive_rules: response.result.is_active ? stats.inactive_rules - 1 : stats.inactive_rules + 1
        });
      }
    } catch (err) {
      console.error('Error toggling auto-response:', err);

      // Revert optimistic update on error
      setAutoResponses(prevResponses =>
        prevResponses.map(rule =>
          rule.id === id ? { ...rule, is_active: !rule.is_active } : rule
        )
      );

      // Show specific error message
      const errorMsg = err.response?.data?.detail || 'Failed to toggle auto-response rule. Please try again.';
      setError(errorMsg);

      // Auto-clear error after 5 seconds
      setTimeout(() => setError(''), 5000);
    } finally {
      setTogglingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      trigger_type: 'lead_created',
      email_template: '',
      is_active: true,
      send_to_lead: true,
      send_to_agent: false,
      delay_minutes: 0,
      lead_source_filter: [],
      priority_filter: []
    });
    setEditingRule(null);
    setError('');
  };

  const handleSourceFilterChange = (source) => {
    const current = formData.lead_source_filter || [];
    if (current.includes(source)) {
      setFormData({
        ...formData,
        lead_source_filter: current.filter(s => s !== source)
      });
    } else {
      setFormData({
        ...formData,
        lead_source_filter: [...current, source]
      });
    }
  };

  const handlePriorityFilterChange = (priority) => {
    const current = formData.priority_filter || [];
    if (current.includes(priority)) {
      setFormData({
        ...formData,
        priority_filter: current.filter(p => p !== priority)
      });
    } else {
      setFormData({
        ...formData,
        priority_filter: [...current, priority]
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-3 sm:p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-3 sm:space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Automated Email Responses</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Configure automatic email responses for different triggers</p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button
              onClick={handleRefresh}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Auto-Response Rule</span>
              <span className="sm:hidden">New Rule</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
            <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Total Rules</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{stats.total_rules}</p>
                </div>
                <Activity className="w-8 h-8 text-[#55C5D0]" />
              </div>
            </div>

            <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Active Rules</p>
                  <p className="text-xl sm:text-2xl font-bold text-green-600 mt-1">{stats.active_rules}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Inactive Rules</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-400 mt-1">{stats.inactive_rules}</p>
                </div>
                <XCircle className="w-8 h-8 text-gray-400" />
              </div>
            </div>

            <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Emails Sent</p>
                  <p className="text-xl sm:text-2xl font-bold text-[#55C5D0] mt-1">{stats.total_emails_sent}</p>
                </div>
                <Mail className="w-8 h-8 text-[#55C5D0]" />
              </div>
            </div>
          </div>
        )}

        {/* Auto-Response Rules List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Auto-Response Rules</h2>
          </div>

          <div className="divide-y divide-gray-200">
            {autoResponses.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-lg font-medium">No auto-response rules yet</p>
                <p className="text-sm mt-1">Create your first rule to start automating email responses</p>
              </div>
            ) : (
              autoResponses.map((rule) => (
                <div key={rule.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-0">
                    <div className="flex-1 w-full sm:w-auto">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">{rule.name}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${rule.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                          }`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600">
                        <p><span className="font-medium">Trigger:</span> {rule.trigger_type_display}</p>
                        <p><span className="font-medium">Template:</span> {rule.email_template_details?.name || 'N/A'}</p>
                        <p>
                          <span className="font-medium">Sends to:</span>{' '}
                          {rule.send_to_lead && 'Lead'}{rule.send_to_lead && rule.send_to_agent && ' & '}
                          {rule.send_to_agent && 'Agent'}
                        </p>
                        {rule.delay_minutes > 0 && (
                          <p><span className="font-medium">Delay:</span> {rule.delay_minutes} minutes</p>
                        )}
                        <p className="text-[#55C5D0] font-medium">
                          Triggered {rule.times_triggered} time{rule.times_triggered !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-4 justify-end">
                      <button
                        onClick={() => handleToggle(rule.id)}
                        disabled={togglingId === rule.id}
                        className={`p-2 rounded hover:bg-gray-100 ${rule.is_active ? 'text-green-600' : 'text-gray-400'
                          } ${togglingId === rule.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={togglingId === rule.id ? 'Toggling...' : (rule.is_active ? 'Deactivate' : 'Activate')}
                      >
                        {togglingId === rule.id ? (
                          <RefreshCw className="w-6 h-6 animate-spin" />
                        ) : rule.is_active ? (
                          <ToggleRight className="w-6 h-6" />
                        ) : (
                          <ToggleLeft className="w-6 h-6" />
                        )}
                      </button>
                      <button
                        onClick={() => handleEdit(rule)}
                        className="p-2 text-[#55C5D0] rounded hover:bg-[#55C5D0]/10"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(rule)}
                        className="p-2 text-red-600 rounded hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Modal for Create/Edit */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingRule ? 'Edit' : 'Create'} Auto-Response Rule
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Rule Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    placeholder="e.g., Welcome Email for New Leads"
                    required
                  />
                </div>

                {/* Trigger Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trigger Event *
                  </label>
                  <select
                    value={formData.trigger_type}
                    onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    required
                  >
                    {triggerOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Email Template */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Template *
                  </label>
                  <select
                    value={formData.email_template}
                    onChange={(e) => setFormData({ ...formData, email_template: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    required
                  >
                    <option value="">Select a template...</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.trigger_type}) {!template.status && '- DRAFT'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sending Options */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Send To
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.send_to_lead}
                        onChange={(e) => setFormData({ ...formData, send_to_lead: e.target.checked })}
                        className="w-4 h-4 text-[#55C5D0] rounded focus:ring-[#55C5D0]"
                      />
                      <span className="text-sm text-gray-700">Send to Lead/Contact</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.send_to_agent}
                        onChange={(e) => setFormData({ ...formData, send_to_agent: e.target.checked })}
                        className="w-4 h-4 text-[#55C5D0] rounded focus:ring-[#55C5D0]"
                      />
                      <span className="text-sm text-gray-700">Send copy to Assigned Agent</span>
                    </label>
                  </div>
                </div>

                {/* Delay */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delay (minutes)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1440"
                    value={formData.delay_minutes}
                    onChange={(e) => setFormData({ ...formData, delay_minutes: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#55C5D0] focus:border-transparent"
                    placeholder="0 = Immediate"
                  />
                  <p className="text-xs text-gray-500 mt-1">0 = Send immediately (max: 1440 = 24 hours)</p>
                </div>

                {/* Lead Source Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lead Source Filter (optional)
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {sourceOptions.map(source => (
                      <label key={source} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={(formData.lead_source_filter || []).includes(source)}
                          onChange={() => handleSourceFilterChange(source)}
                          className="w-4 h-4 text-[#55C5D0] rounded focus:ring-[#55C5D0]"
                        />
                        <span className="text-sm text-gray-700 capitalize">{source}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave empty to trigger for all sources</p>
                </div>

                {/* Priority Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority Filter (optional)
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {priorityOptions.map(priority => (
                      <label key={priority} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={(formData.priority_filter || []).includes(priority)}
                          onChange={() => handlePriorityFilterChange(priority)}
                          className="w-4 h-4 text-[#55C5D0] rounded focus:ring-[#55C5D0]"
                        />
                        <span className="text-sm text-gray-700 capitalize">{priority}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Leave empty to trigger for all priorities</p>
                </div>

                {/* Active Status */}
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-[#55C5D0] rounded focus:ring-[#55C5D0]"
                    />
                    <span className="text-sm font-medium text-gray-700">Rule is Active</span>
                  </label>
                </div>

                {/* Error Display */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">
                    {error}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 bg-[#55C5D0] text-white rounded-lg hover:bg-[#3bb3be] text-sm sm:text-base"
                  >
                    {editingRule ? 'Update' : 'Create'} Rule
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && ruleToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Delete Auto-Response Rule
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete the rule <span className="font-medium">"{ruleToDelete.name}"</span>? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setRuleToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoEmailResponses;

